import { FunctionComponent, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Navbar, Nav, Dropdown } from 'react-bootstrap'
import { Person, BoxArrowRight } from 'react-bootstrap-icons'

import classnames from 'classnames'
import headerCss from './header.module.scss'
import logo from 'assets/images/logo.png'
import { UserRole } from 'features/user/types'
import {
  getHospitalAdmin,
  getHospitalSynchronizedAt,
  getUserProfile,
  getLoggedUser,
  isAuthenticated,
  setLoggedUser,
  parseHospitalSynchronizedAtMs,
} from '../../features/user/auth'
import * as userApi from '../../features/user/api.client'
import { hasActiveBi } from 'features/doctors/types'

const formatSincronizedAt = (raw: string | null): string | null => {
  const millis = parseHospitalSynchronizedAtMs(raw)

  if (millis === null) {
    return null
  }

  const date = new Date(millis)

  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Header used when the user is logged in
 */
const Header: FunctionComponent<{ logout: () => void }> = ({ logout }) => {
  const navigate = useNavigate()
  const [userEmail, setUserEmail] = useState(getLoggedUser()?.email ?? null)
  const [showBiMenu, setShowBiMenu] = useState(false)

  const isUserDoctor = (): boolean => getUserProfile() === UserRole.DOCTOR

  const isUserHospitalAdmin = (): boolean =>
    getUserProfile() === UserRole.HOSPITAL_ADMIN

  const isUserAdmin = (): boolean => getUserProfile() === UserRole.ADMIN

  const hospitalSyncLabel =
    isUserHospitalAdmin() && formatSincronizedAt(getHospitalSynchronizedAt())

  const openDoctorPage = () => navigate('/doctor')

  useEffect(() => {
    if (isUserDoctor()) {
      userApi
        .get()
        .then((user) => {
          setLoggedUser(user)
          setUserEmail(user.email)
          setShowBiMenu(hasActiveBi(user))
        })
        .catch((error) => {
          console.error('Não foi possível carregar o email do usuário.', error)
          setShowBiMenu(false)
        })
      return
    }

    setShowBiMenu(false)

    const loggedUser = getLoggedUser()

    if (loggedUser?.email) {
      setUserEmail(loggedUser.email)
      return
    }

    if (!isAuthenticated()) {
      return
    }

    userApi
      .get()
      .then((user) => {
        setLoggedUser(user)
        setUserEmail(user.email)
      })
      .catch((error) => {
        console.error('Não foi possível carregar o email do usuário.', error)
      })
  }, [])

  return (
    <Navbar expand="lg" className={headerCss.customNavBar}>
      <Navbar.Brand href="#" className={headerCss.navbarBrand}>
        <span className={headerCss.brandPrimary}>
          <img
            src={logo}
            width="30"
            height="30"
            className={classnames('d-inline-block', headerCss.brandLogo)}
            alt="Clic-repasse"
          />
          <span className={headerCss.brandMain}>Clic-repasse</span>
        </span>
        {isUserHospitalAdmin() && (
          <span className={headerCss.hospitalBlock}>
            <b className={headerCss.hospitalName}>{getHospitalAdmin()}</b>
            {hospitalSyncLabel ? (
              <span className={headerCss.hospitalSyncAt}>
                Atualizado: {hospitalSyncLabel}
              </span>
            ) : null}
          </span>
        )}
      </Navbar.Brand>

      <Nav className={classnames('mr-auto', headerCss.customNav)}>
        {(isUserDoctor() || isUserHospitalAdmin()) && (
          <Link
            className={classnames('nav-link', headerCss.customNavLink)}
            to="/transfers"
          >
            Repasses
          </Link>
        )}
        {showBiMenu && (
          <Link
            className={classnames('nav-link', headerCss.customNavLink)}
            to="/bi-medico"
          >
            Bi Médico
          </Link>
        )}
        {(isUserDoctor() || isUserAdmin()) && (
          <Link
            className={classnames('nav-link', headerCss.customNavLink)}
            to="/hospitals"
          >
            Hospitais
          </Link>
        )}

        {isUserAdmin() && (
          <Link
            className={classnames('nav-link', headerCss.customNavLink)}
            to="/users"
          >
            Usuários
          </Link>
        )}

        {isUserHospitalAdmin() && (
          <Link
            className={classnames('nav-link', headerCss.customNavLink)}
            to="/access-requests"
          >
            Solicitações de acesso
          </Link>
        )}
        {isUserHospitalAdmin() && (
          <Link
            className={classnames('nav-link', headerCss.customNavLink)}
            to="/doctors"
          >
            Médicos
          </Link>
        )}
      </Nav>
      <Navbar.Collapse id="nva-user-name" className="justify-content-end">
        <Nav>
          <Dropdown
            as={Nav.Item}
            align="end"
            className={headerCss.profileDropdown}
          >
            <Dropdown.Toggle
              as={Nav.Link}
              className={headerCss.profileToggleWrap}
              aria-label="Perfil do usuário"
            >
              <span className={headerCss.profileToggle}>
                <Person />
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu className={headerCss.profileMenu}>
              {isUserDoctor() && (
                <>
                  <Dropdown.Item href="#" onClick={openDoctorPage}>
                    Meus dados
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              )}
              <Dropdown.ItemText as="div" className={headerCss.userInfo}>
                <div className={headerCss.userInfoLabel}>
                  Logado com o email:
                </div>
                <div className={headerCss.userInfoEmail}>
                  {userEmail ?? '—'}
                </div>
              </Dropdown.ItemText>
              <Dropdown.Item
                href="#"
                onClick={logout}
                className={headerCss.logoutItem}
              >
                <BoxArrowRight className={headerCss.logoutIcon} />
                Sair
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  )
}

export default Header
